import app from 'flarum/forum/app';
import Icon from 'flarum/common/components/Icon';
import Link from 'flarum/common/components/Link';
import Placeholder from 'flarum/common/components/Placeholder';
import humanTime from 'flarum/common/helpers/humanTime';
import classList from 'flarum/common/utils/classList';
/** Matches flarum/realtime's TypingActivity channel and event. */
const CHANNEL = 'private-typing-activity';
const EVENT = 'typing-activity';

/** Same expiry as realtime's typing indicator: no ping for this long means they stopped. */
const ACTIVE_MS = 6000;
const HISTORY = 30;
/** Past typing drops off after this long. */
const MAX_AGE_MS = 15 * 60 * 1000;
/** How often to look for entries that have aged out while the forum was quiet. */
const PRUNE_EVERY_MS = 60 * 1000;
/**
 * Who is typing where, forum-wide, from flarum/realtime's typing-activity
 * channel. The server only sends each subscriber what they could see anyway
 * (discussions they can see, names of members hiding their online status only
 * with `user.viewLastSeenAt`), so this renders what arrives as it is.
 */
export default class TypingActivitySource {
  entries = [];
  socket = null;
  channel = null;
  requested = (() => new Set())();
  expiryTimer = null;
  pruneTimer = null;
  load() {
    return Promise.resolve();
  }

  /** Subscribes, or re-subscribes after realtime has replaced its socket. */
  start() {
    const socket = app.websocket;
    if (!socket || socket === this.socket) return;
    this.stop();
    this.socket = socket;
    this.channel = socket.subscribe(CHANNEL);
    this.channel.bind(EVENT, this.onActivity);
    this.pruneTimer = window.setInterval(() => this.prune() && m.redraw(), PRUNE_EVERY_MS);
  }
  stop() {
    this.channel?.unbind(EVENT, this.onActivity);
    this.socket?.unsubscribe(CHANNEL);
    this.socket = this.channel = null;
    if (this.expiryTimer) clearTimeout(this.expiryTimer);
    if (this.pruneTimer) clearInterval(this.pruneTimer);
    this.expiryTimer = this.pruneTimer = null;
  }

  /** Drops entries older than MAX_AGE_MS; true if any went. */
  prune() {
    const cutoff = Date.now() - MAX_AGE_MS;
    const before = this.entries.length;
    this.entries = this.entries.filter(entry => entry.at.getTime() >= cutoff);
    return this.entries.length !== before;
  }

  /** Fed by its own channel; nothing else concerns it. */
  onRealtime() {}
  checkForNew() {
    return Promise.resolve(0);
  }
  showNew() {
    return Promise.resolve();
  }
  view() {
    if (!this.socket) {
      return m(Placeholder, {
        text: app.translator.trans('flarum-deck.forum.typing.offline_text')
      });
    }
    if (!this.entries.length) {
      return m(Placeholder, {
        text: app.translator.trans('flarum-deck.forum.typing.empty_text')
      });
    }
    const now = Date.now();
    return m("ul", {
      className: "DeckTyping"
    }, this.entries.map(entry => {
      const active = now - entry.at.getTime() < ACTIVE_MS;
      return m("li", {
        key: entry.key,
        className: classList('DeckTyping-item', {
          'DeckTyping-item--active': active
        })
      }, m(Icon, {
        name: "fas fa-keyboard",
        className: "DeckTyping-icon"
      }), m("div", {
        className: "DeckTyping-text"
      }, this.describe(entry, active), !active && m("span", {
        className: "DeckTyping-time"
      }, humanTime(entry.at))));
    }));
  }
  describe(entry, active) {
    const user = entry.userId ? app.store.getById('users', String(entry.userId)) : null;
    const nameText = entry.displayName ?? app.translator.trans('flarum-deck.forum.typing.someone');
    const name = m("strong", null, user ? m(Link, {
      href: app.route('user', {
        username: user.username()
      })
    }, nameText) : nameText);
    if (entry.discussionId) {
      const discussion = app.store.getById('discussions', String(entry.discussionId));
      const link = discussion ? m(Link, {
        href: app.route.discussion(discussion)
      }, discussion.title()) : '…';
      return active ? app.translator.trans('flarum-deck.forum.typing.replying_text', {
        name,
        discussion: link
      }) : app.translator.trans('flarum-deck.forum.typing.was_replying_text', {
        name,
        discussion: link
      });
    }
    const tags = (entry.tagIds ?? []).map(id => app.store.getById('tags', String(id))?.name()).filter(Boolean).join(', ');
    if (!tags) {
      return active ? app.translator.trans('flarum-deck.forum.typing.starting_text', {
        name
      }) : app.translator.trans('flarum-deck.forum.typing.was_starting_text', {
        name
      });
    }
    return active ? app.translator.trans('flarum-deck.forum.typing.starting_in_text', {
      name,
      tags
    }) : app.translator.trans('flarum-deck.forum.typing.was_starting_in_text', {
      name,
      tags
    });
  }
  onActivity = data => {
    const key = `${data.userId ?? 'someone'}:${data.discussionId ?? 'new'}`;
    this.entries = [{
      ...data,
      key,
      at: new Date()
    }, ...this.entries.filter(entry => entry.key !== key)].slice(0, HISTORY);
    this.prune();
    if (data.discussionId && !this.requested.has(data.discussionId)) {
      this.requested.add(data.discussionId);
      if (!app.store.getById('discussions', String(data.discussionId))) {
        app.store.find('discussions', String(data.discussionId)).then(() => m.redraw()).catch(() => {});
      }
    }

    // Redraw once more after it lapses, so "is replying" becomes "was replying".
    if (this.expiryTimer) clearTimeout(this.expiryTimer);
    this.expiryTimer = window.setTimeout(() => m.redraw(), ACTIVE_MS + 100);
    m.redraw();
  };
}
flarum.reg.add('flarum-deck', 'forum/columns/TypingActivitySource', TypingActivitySource);